/**
 * Hide the header on scroll down, show it on scroll up (build log row 44).
 *
 * Atelier's own "on scroll up" sticky mode scrolls the header away with the page
 * and fades it back in, and it has no guard for open menus or drawers, so it is
 * not used. Here the header stays `position: sticky` (its space is always
 * reserved, so nothing shifts) and this module only toggles
 * `data-bettino-hidden` on `<header-component>`; bettino-global.css turns that
 * into a translateY(-100%) on the sticky wrapper.
 *
 * Rules:
 *  - Visible while the scroll position is inside the header's own height.
 *  - Past that, a downward move of more than THRESHOLD_PX hides it; an upward
 *    move of more than THRESHOLD_PX shows it.
 *  - Never hidden while a drawer, dialog, search, the mobile menu or a top-level
 *    dropdown is open. Keyboard focus inside the header always reveals it.
 */
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const THRESHOLD_PX = 8;
const HIDDEN_ATTRIBUTE = 'data-bettino-hidden';

/** Anything that is open and depends on the header being on screen. */
const OPEN_SURFACE_SELECTOR = [
  'html[scroll-lock]',
  'dialog[open]',
  '#Details-menu-drawer-container[open]',
  'header-menu[aria-expanded="true"]',
  '#header-component [aria-expanded="true"]',
].join(',');

/** @type {EventTarget | null} */
let scrollTarget = null;
let lastScrollTop = 0;
let rafId = 0;

function getHeader() {
  return document.querySelector('#header-component');
}

/** @returns {boolean} */
function isSurfaceOpen() {
  return document.querySelector(OPEN_SURFACE_SELECTOR) !== null;
}

/** @param {Element} header */
function show(header) {
  header.removeAttribute(HIDDEN_ATTRIBUTE);
}

/** @param {Element} header */
function hide(header) {
  if (header.hasAttribute(HIDDEN_ATTRIBUTE)) return;

  // Nothing normally reaches here with a dropdown open (see isSurfaceOpen), but a
  // hidden header must never leave its panel behind.
  const menu = /** @type {any} */ (header.querySelector('header-menu[aria-expanded="true"]'));
  if (typeof menu?.deactivate === 'function') menu.deactivate(new Event('bettino-header-hide'));

  header.setAttribute(HIDDEN_ATTRIBUTE, '');
}

/**
 * The announcement bar sits above the sticky header and scrolls away with the page,
 * so while it is on screen the header's top edge is that much below the viewport top.
 * Fixed layers that open flush under the header (the mobile menu) read this as
 * `--bettino-announcement-visible`. Written only when it changes.
 */
let announcementVisible = -1;

function updateAnnouncementOffset() {
  const bar = document.querySelector('.bettino-announcement');
  const visible = bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 0;
  if (visible === announcementVisible) return;

  announcementVisible = visible;
  document.documentElement.style.setProperty('--bettino-announcement-visible', `${visible}px`);
}

function update() {
  rafId = 0;

  updateAnnouncementOffset();

  const header = getHeader();
  if (!header) return;

  const scrollTop = getScrollTop();
  const headerHeight = header.getBoundingClientRect().height;

  if (scrollTop < headerHeight || isSurfaceOpen()) {
    show(header);
    lastScrollTop = scrollTop;
    return;
  }

  const delta = scrollTop - lastScrollTop;
  if (Math.abs(delta) <= THRESHOLD_PX) return;

  if (delta > 0) {
    hide(header);
  } else {
    show(header);
  }
  lastScrollTop = scrollTop;
}

function onScroll() {
  if (rafId) return;
  rafId = requestAnimationFrame(update);
}

function bindScroll() {
  scrollTarget?.removeEventListener('scroll', onScroll);
  scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  lastScrollTop = getScrollTop();
}

function init() {
  bindScroll();
  updateAnnouncementOffset();
  window.addEventListener('resize', updateAnnouncementOffset, { passive: true });

  // The scroll container changes at the squeeze breakpoint (see scroll-container.js).
  scrollContainerMediaQuery.addEventListener('change', bindScroll);

  // Keyboard users tabbing into the header must be able to see it.
  document.addEventListener('focusin', (event) => {
    const header = getHeader();
    if (header && event.target instanceof Node && header.contains(event.target)) show(header);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
